import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL query for fetching paginated games with assignment
export const GET_GAMES_WITH_ASSIGNMENT_QUERY = gql`
  query GamesWithAssignment(
    $where: GameWithAssignmentWhereInput!
    $pagination: GamePaginationArgs
  ) {
    gamesWithAssignment(where: $where, pagination: $pagination) {
      games {
        id
        name
        gameId
        providerId
        categoryId
        launchUrl
        demoAvailable
        freespinAvailable
        deviceType
        betType
        linesCount
        isActive
        iconUrl
        labelIconUrl
        description
        createdAt
        position
        comment
        orientation
        categoryType
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
        total
      }
    }
  }
`;

// Types for the response
export interface Game {
  id: number;
  name: string;
  gameId: string;
  providerId: number;
  categoryId: number;
  launchUrl?: string | null;
  demoAvailable: boolean;
  freespinAvailable: boolean;
  deviceType: string;
  betType?: string | null;
  linesCount?: number | null;
  isActive: boolean;
  iconUrl?: string | null;
  labelIconUrl?: string | null; // per-label override, already applied to iconUrl
  description?: string | null;
  createdAt: string;
  position: number;
  comment?: string | null;
  orientation?: string
  categoryType?: string
}

export interface PageInfo {
  startCursor?: string;
  endCursor?: string;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  total: number;
}

export interface GamesPaginatedResponse {
  games: Game[];
  pageInfo: PageInfo;
}

// Raw GraphQL response interface (before transformation)
export interface RawGame {
  id: string; // GraphQL ID fields are strings
  name: string;
  gameId: string;
  providerId: number;
  categoryId: number;
  launchUrl?: string | null;
  demoAvailable: boolean;
  freespinAvailable: boolean;
  deviceType: string;
  betType?: string | null;
  linesCount?: number | null;
  isActive: boolean;
  iconUrl?: string | null;
  labelIconUrl?: string | null; // per-label override, already applied to iconUrl
  description?: string | null;
  createdAt: string;
  position: number;
  comment?: string | null;
  orientation?: string;
  categoryType?: string
}

export interface RawGamesPaginatedResponse {
  games: RawGame[];
  pageInfo: PageInfo;
}

export interface GetGamesWithAssignmentResponse {
  gamesWithAssignment: RawGamesPaginatedResponse;
}

// GetAllGames service class
export class GetAllGamesService {
  private static instance: GetAllGamesService;

  private constructor() { }

  public static getInstance(): GetAllGamesService {
    if (!GetAllGamesService.instance) {
      GetAllGamesService.instance = new GetAllGamesService();
    }
    return GetAllGamesService.instance;
  }

  // Fetch games with assignment using labelId, environmentId, and providerId
  async getGamesWithAssignment(
    labelId: number,
    environmentId: number,
    providerId?: number,
    first: number = 100,
    after?: string,
    categoryType?: string,
    brandname?: string,
    search?: string,
  ): Promise<GamesPaginatedResponse> {
    try {
      const where: any = {
        labelId,
        environmentId,
      };
      if (providerId !== undefined) {
        where.providerId = providerId;
      }
      if (categoryType) {
        where.categoryType = categoryType;
      }
      if (brandname?.toLowerCase() !== 'all' && brandname !== undefined && brandname !== null && brandname !== '') {
        where.brandname = brandname;
      }
      if (search && search.trim() !== '') {
        where.search = search.trim();
      }
      const { data } = await apolloClient.query<GetGamesWithAssignmentResponse>(
        {
          query: GET_GAMES_WITH_ASSIGNMENT_QUERY,
          variables: {
            where,
            pagination: { first, ...(after && { after }) },
          },
          fetchPolicy: 'network-only',
        },
      );

      // Transform the response to convert string IDs to numbers
      const transformedGames = data.gamesWithAssignment.games.map((game) => ({
        ...game,
        id: Number(game.id),
      }));

      return {
        ...data.gamesWithAssignment,
        games: transformedGames,
      };
    } catch (error: any) {
      console.error('Error fetching games with assignment:', error);
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

  // Legacy method for backward compatibility
  async getAllGames(
    providerId: number,
    first: number = 10,
    after: number = 0,
  ): Promise<GamesPaginatedResponse> {
    // Default to labelId: 1, environmentId: 2 for backward compatibility
    return this.getGamesWithAssignment(1, 2, providerId, first);
  }

  // Fetch games with custom filters
  async getGamesWithFilters(
    where: any,
    pagination: any,
  ): Promise<GamesPaginatedResponse> {
    try {
      const { data } = await apolloClient.query<GetGamesWithAssignmentResponse>(
        {
          query: GET_GAMES_WITH_ASSIGNMENT_QUERY,
          variables: {
            where,
            pagination,
          },
          fetchPolicy: 'network-only',
        },
      );

      // Transform the response to convert string IDs to numbers
      const transformedGames = data.gamesWithAssignment.games.map((game) => ({
        ...game,
        id: Number(game.id),
      }));

      return {
        ...data.gamesWithAssignment,
        games: transformedGames,
      };
    } catch (error: any) {
      console.error('Error fetching games with filters:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const getAllGamesService = GetAllGamesService.getInstance();

// Export convenience function - updated to use new parameters
export async function getAllGames(
  labelId: number = 1,
  environmentId: number = 2,
  providerId?: number,
  first: number = 100,
  after?: string,
  categoryType?: string,
  brandname?: string,
  search?: string,
): Promise<GamesPaginatedResponse> {
  return getAllGamesService.getGamesWithAssignment(
    labelId,
    environmentId,
    providerId,
    first,
    after,
    categoryType,
    brandname,
    search,
  );
}
